import React, { createContext, useCallback, useState, useMemo, useRef } from "react";
import api from "../services/axios";
import UserPadrao from "../assets/participantes/user_padrao.jpg";
import UserPadraoM from "../assets/participantes/Ícone-1-200x200.jpg";
import UserPadraoF from "../assets/participantes/Ícone-2-200x200.jpg";
import useAuth from "../hooks/useAuthContext";
import { toast } from "react-toastify";
import { NOTIFICACOES_MIN_INTERVALO_MS } from "../hooks/useNotificacoesPolling";

const AppContext = createContext({});
export default AppContext;

export const AppProvider = ({ children }) => {
  const { usuario } = useAuth();
  const [mostraMenuAdm, setMostraMenuAdm] = useState(true);
  const [apresentarModal, setApresentarModal] = useState(false);

  const [notificacoes, setNotificacoes] = useState([]);
  const [notificacoesUsuario, setNotificacoesUsuario] = useState([]);
  const [formValido, setFormValido] = useState(false);
  const [formValidoPesquisa, setFormValidoPesquisa] = useState(false);
  const [formValidoBio, setFormValidoBio] = useState(false);
  const [pesquisasUsuario, setPesquisasUsuario] = useState([]);
  const [tipoUsuario, setTipoUsuario] = useState(null);

  const ultimaConsultaRef = useRef(0);
  const consultaEmAndamentoRef = useRef(null);

  // Retorna true quando as listas são equivalentes (mesmos ids + status).
  const compararNotificacoes = useCallback((prev, novas) => {
    if (prev.length !== novas.length) return false;

    const prevPorId = new Map(prev.map((n) => [n.id, n]));
    for (const nova of novas) {
      const atual = prevPorId.get(nova.id);
      if (!atual) return false;
      if (Number(atual.status) !== Number(nova.status)) return false;
    }
    return true;
  }, []);

  const toggleModal = useCallback(() => {
    try {
      setApresentarModal(!apresentarModal);
    } catch (error) {
      console.log("toggleModal error:", error, error.response?.data);
    }
  }, [apresentarModal]);

  const getNotificacoes = useCallback(async () => {
    try {
      const resp = await api.get("/notificacoes");
      if (resp.status === 200) {
        setNotificacoes(resp.data.dados);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  }, []);

  /**
   * Consulta notificações do usuário com throttle e dedupe.
   * @param {{ force?: boolean }} [opcoes]
   * - force: ignora intervalo mínimo e aba oculta (ex.: abrir o modal)
   */
  const consultarNotificacoes = useCallback(async (opcoes = {}) => {
    const force = Boolean(opcoes?.force);

    try {
      if (usuario && usuario?.tipo === "recepcao") return;

      if (!force && typeof document !== "undefined" && document.hidden) {
        return;
      }

      const agora = Date.now();
      if (
        !force &&
        ultimaConsultaRef.current > 0 &&
        agora - ultimaConsultaRef.current < NOTIFICACOES_MIN_INTERVALO_MS
      ) {
        return;
      }

      if (consultaEmAndamentoRef.current) {
        return consultaEmAndamentoRef.current;
      }

      const promise = (async () => {
        const resp = await api.get("/notificacoes-usuario");
        if (resp.status === 200) {
          const auxNotificacores = resp.data.dados || [];
          ultimaConsultaRef.current = Date.now();

          setNotificacoesUsuario((prevNotificacoes) => {
            // Evita regressão: se já marcamos como lida localmente, não volta a "não lida"
            // por uma resposta atrasada da API.
            const mescladas = auxNotificacores.map((nova) => {
              const local = prevNotificacoes.find((p) => p.id === nova.id);
              if (
                local &&
                Number(local.status) === 1 &&
                Number(nova.status) === 0
              ) {
                return { ...nova, status: 1 };
              }
              return nova;
            });

            if (!compararNotificacoes(prevNotificacoes, mescladas)) {
              return mescladas;
            }
            return prevNotificacoes;
          });
        }
      })();

      consultaEmAndamentoRef.current = promise;
      try {
        await promise;
      } finally {
        consultaEmAndamentoRef.current = null;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
      consultaEmAndamentoRef.current = null;
    }
  }, [usuario, compararNotificacoes]);

  const novaNotificacao = useCallback(async (texto) => {
    try {
      const resp = await api.post("/notificacoes", { texto, tipo: "geral" });
      if (resp.status === 200) {
        getNotificacoes();
        consultarNotificacoes({ force: true });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("login error:", error, error.response?.data);
    }
  }, [getNotificacoes, consultarNotificacoes]);

  const visualizarNotificacao = useCallback(async (id_notificacao) => {
    let jaVisualizada = false;

    setNotificacoesUsuario((prev) => {
      const atual = prev.find((n) => n.id === id_notificacao);
      if (!atual || Number(atual.status) === 1) {
        jaVisualizada = true;
        return prev;
      }
      return prev.map((n) =>
        n.id === id_notificacao ? { ...n, status: 1 } : n
      );
    });

    if (jaVisualizada) return;

    try {
      await api.post("/notificacoes-visualizar", {
        id: id_notificacao,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
      consultarNotificacoes({ force: true });
    }
  }, [consultarNotificacoes]);

  const getPatrocinadores = async () => {
    try {
      const resp = await api.get("/patrocinador");
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getPatrocinadoresLista = async () => {
    try {
      const resp = await api.get("/patrocinador-lista");
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getUsuariosLista = useCallback(async () => {
    try {
      const resp = await api.get("/usuarios-lista");
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
      throw error;
    }
    return [];
  }, []);

  const getPatrocinador = async (id_patrocinador) => {
    try {
      const resp = await api.get("/patrocinador/" + id_patrocinador);
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getPlanos = useCallback(async () => {
    try {
      const resp = await api.get("/planos");
      if (resp.status === 200 && resp.data?.dados) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
    return [];
  }, []);

  const getUsuario = async (id_usuario) => {
    try {
      const resp = await api.post("/usuarios", { id: id_usuario });
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const insertUsuarioAdm = async (
    nome,
    cpf,
    dt_nascimento,
    email,
    tipo,
    nome_cracha,
    sexo,
    celular,
    empresa,
    cargo,
    telefone_comercial,
    departamento,
    tipo_instituicao,
    segmento_outros,
    id_patrocinador
  ) => {
    try {
      let user = {
        nome,
        cpf,
        dt_nascimento,
        email,
        tipo,
        nome_cracha,
        sexo,
        celular,
        empresa,
        cargo,
        telefone_comercial,
        departamento,
        tipo_instituicao,
        segmento_outros,
        id_patrocinador,
      };

      const resp = await api.post("/usuarios-create/", user);
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const atualizaPatrocinadorAdm = async (
    patrocinador,
    perfil,
    email,
    logo,
    segmento,
    idPlano,
    id,
    sequencia
  ) => {
    try {
      const formData = new FormData();

      formData.append("patrocinador", patrocinador);
      formData.append("perfil", perfil);
      formData.append("email", email);
      formData.append("segmento", segmento);
      if (idPlano !== "" && idPlano !== null && idPlano !== undefined) {
        formData.append("id_plano", String(idPlano));
      }
      if (sequencia !== "" && sequencia !== null && sequencia !== undefined) {
        formData.append("sequencia", String(sequencia));
      }
      if (logo) {
        formData.append("logo", logo);
      }
      if (id) {
        formData.append("id", id);
      }

      const resp = await api.post("/patrocinador-update/", formData, {
        headers: {
          "content-type": "multipart/form-data",
        },
      });
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getEventos = async (dia) => {
    try {
      const resp = await api.post("/agenda", {
        tipo: [
          "conteudo_patrocinado",
          "esporte",
          "conteudo_editorial",
          "extraordinaria",
        ],
        dia,
        adm: true,
      });
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getAgenda = async (dia) => {
    try {
      const resp = await api.post("/agenda", {
        tipo: [
          "conteudo_patrocinado",
          "conteudo_editorial",
          "esporte",
          "geral",
        ],
        dia,
        adm: true,
      });
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getDiasEvento = async () => {
    try {
      const resp = await api.get("/agenda-dias-evento");
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
    return { evento: null, dias: [] };
  };

  const getAgendaRecepcao = async (dia, patrocinador, tipo) => {
    try {
      let resp;
      if (patrocinador) {
        resp = await api.post("/agenda-recepcao", {
          tipo,
          dia,
          patrocinador,
        });
      } else {
        resp = await api.post("/agenda-recepcao-conteudo", {
          tipo,
          dia,
          patrocinador,
        });
      }
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getReunioesNegocio = async () => {
    try {
      const resp = await api.post("/agenda", {
        tipo: ["reuniao_negocio","m_m"],
        adm: true,
      });
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getEventoId = async (id) => {
    try {
      const resp = await api.post("/agenda", {
        id,
        adm: true,
      });
      if (resp.status === 200) {
        return resp.data.dados[0];
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getMensagens = async () => {
    try {
      const resp = await api.get("/chat-conversas");
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getMensagensChat = async (id_usuario) => {
    try {
      const resp = await api.get("/chat-conversas/" + id_usuario);
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getIndicadoresMatching = useCallback(async (patrocinadorId = null) => {
    try {
      const params = {};
      if (patrocinadorId != null && patrocinadorId !== "") {
        params.patrocinadorId = patrocinadorId;
      }
      const resp = await api.get("/indicadores-machting", { params });
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error indicadores-matching:", error, error.response?.data);
      return null;
    }
  }, []);

  const enviaMenagem = async (id_usuario, mensagem) => {
    try {
      let dados = {
        destino: id_usuario,
        mensagem,
      };
      const resp = await api.post("/chat-mensagens/", dados);
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const visualizarMenagem = async (id_chat_mensagem) => {
    try {
      let dados = {
        id_chat_mensagem,
      };
      const resp = await api.post("/chat-visualizar/", dados);
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const verificaDispositivo = useCallback(() => {
    let navigatorObject = window.navigator;
    // noinspection RedundantIfStatementJS
    if (navigatorObject.userAgent.includes("iPhone")) {
      return true;
    }
    return false;
  }, []);

  const handleImagemParticipante = useCallback((usuarioImg) => {
    if (usuarioImg?.avatar && usuarioImg?.avatar !== "") {
      return usuarioImg?.avatar;
    }
    if (usuarioImg?.sexo === "masculino") {
      return UserPadraoM;
    }
    if (usuarioImg?.sexo === "feminino") {
      return UserPadraoF;
    }
    return UserPadrao;
  }, []);

  const redefinirSenha = async (id_usuario) => {
    try {
      const resp = await api.post("/usuarios-redefinir-senha", {
        id: id_usuario,
      });
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
      return error.response?.data;
    }
  };

  const excluirUsuario = async (id_usuario) => {
    try {
      const resp = await api.post("/usuarios-excluir", {
        id: id_usuario,
      });
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
      return error.response?.data;
    }
  };

  const excluirPatrocinador = async (id_patrocinador) => {
    try {
      const resp = await api.post("/patrocinador-excluir", {
        id: id_patrocinador,
      });
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
      return error.response?.data;
    }
  };

  const criarUsuarioPatrocinadorAdm = async (id_info) => {
    try {
      const resp = await api.post("/patrocinador-criar-usuario", { id_info });
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
      return false
    }
  };

  const excluirAgenda = async (id_agenda) => {
    try {
      const resp = await api.post("/agenda-delete", { id_agenda });
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const verificaPesquisa = async () => {
    try {
      let pesquisas = await api.post("/verifica-pesquisa", {});

      if (pesquisas.status === 200) {
        setPesquisasUsuario(pesquisas.data.pesquisas);
        setTipoUsuario(pesquisas.data.usuarioTipo);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const getPalestrantes = async () => {
    try {
      let ids = [33,34,35,36,37,38,39,40,41,42,43,44,45];
      const resp = await api.post("/usuarios-apenas-user", { ids });
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      console.log("error:", error, error.response?.data);
    }
  };

  const getDocs = async () => {
    try {
      const resp = await api.get("/docs");
      if (resp.status === 200) {
        return resp.data.dados;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const excluirDocs = async (id) => {
    try {
      const resp = await api.delete("/docs/" + id);
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const incluirDocs = async (descricao, detalhes, posicao, doc) => {
    const formData = new FormData();
    formData.append("descricao", descricao);
    formData.append("detalhes", detalhes);
    formData.append("posicao", posicao);
    formData.append("doc", doc);
    try {
      const resp = await api.post("/docs", formData, {
        headers: {
          "content-type": "multipart/form-data",
        },
      });
      if (resp.status === 201) {
        return resp;
      }
    } catch (error) {
      console.log("error:", error, error.response?.data.mensagem);
      if (error.response?.status === 413) {
        toast.error("Erro ao salvar item, arquivo enviado é muito grande.");
      }
    }
  };

  const getDoc = async (id) => {
    try {
      const resp = await api.get("/docs/" + id);
      if (resp.status === 200) {
        return resp.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const alterarDocs = async (id, descricao, detalhes, posicao, doc = null) => {
    const formData = new FormData();
    formData.append("descricao", descricao);
    formData.append("detalhes", detalhes);
    formData.append("posicao", posicao);
    if (doc) {
      formData.append("doc", doc);
    }
    try {
      const resp = await api.put("/docs/" + id, formData, {
        headers: {
          "content-type": "multipart/form-data",
        },
      });
      if (resp.status === 200) {
        return resp;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const addClickDoc = async (id) => {
    try {
      const resp = await api.post("/docs-add-click", { id });
      if (resp.status === 200) {
        return resp;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error:", error, error.response?.data);
    }
  };

  const contextValue = useMemo(() => ({
    apresentarModal,
    toggleModal,
    notificacoes,
    getNotificacoes,
    novaNotificacao,
    notificacoesUsuario,
    visualizarNotificacao,
    consultarNotificacoes,
    getPatrocinadores,
    getEventos,
    getEventoId,
    getMensagens,
    getMensagensChat,
    enviaMenagem,
    getIndicadoresMatching,
    verificaDispositivo,
    handleImagemParticipante,
    visualizarMenagem,
    formValido,
    setFormValido,
    formValidoPesquisa,
    setFormValidoPesquisa,
    formValidoBio,
    setFormValidoBio,
    getUsuario,
    redefinirSenha,
    excluirUsuario,
    excluirPatrocinador,
    getPatrocinador,
    getPlanos,
    insertUsuarioAdm,
    atualizaPatrocinadorAdm,
    getAgenda,
    getDiasEvento,
    getReunioesNegocio,
    mostraMenuAdm,
    setMostraMenuAdm,
    getAgendaRecepcao,
    criarUsuarioPatrocinadorAdm,
    excluirAgenda,
    getPatrocinadoresLista,
    getUsuariosLista,
    verificaPesquisa,
    pesquisasUsuario,
    setPesquisasUsuario,
    tipoUsuario,
    getPalestrantes,
    getDocs,
    excluirDocs,
    incluirDocs,
    getDoc,
    alterarDocs,
    addClickDoc,
  }), [
    apresentarModal,
    toggleModal,
    notificacoes,
    notificacoesUsuario,
    consultarNotificacoes,
    formValido,
    formValidoPesquisa,
    formValidoBio,
    getIndicadoresMatching,
    mostraMenuAdm,
    pesquisasUsuario,
    tipoUsuario,
    getPlanos,
    getUsuariosLista,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
